import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { bankReconciliationAPI, membersAPI } from '../../services/api';

const TABS = [
  { key: 'matched_pending', label: 'Pending → Paid' },
  { key: 'not_found', label: 'Not Found (needs member)' },
  { key: 'mismatch', label: 'Amount Mismatch' },
  { key: 'already_paid', label: 'Already Paid' },
  { key: 'resolved_paid', label: 'Resolved (Paid)' },
  { key: 'resolved_imported', label: 'Resolved (Imported)' },
  { key: 'skipped', label: 'Skipped' },
];

function money(n) {
  return 'TZS ' + Number(n).toLocaleString();
}

function UploadCard({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setMsg('');
    try {
      const res = await bankReconciliationAPI.upload(file);
      const c = res.counts || {};
      setMsg(`Uploaded — ${res.total} transactions found: ${c.matched_pending || 0} pending→paid, ${c.not_found || 0} not found, ${c.mismatch || 0} mismatch, ${c.already_paid || 0} already paid.`);
      setFile(null);
      onUploaded(res.batch_id);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <form onSubmit={handleUpload} className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <input type="file" accept=".xls,.xlsx" onChange={(e) => setFile(e.target.files[0] || null)}
        className="text-sm" />
      <button type="submit" disabled={!file || uploading}
        className="rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold">
        {uploading ? 'Uploading…' : 'Upload Statement'}
      </button>
      {msg && <p className="text-xs text-slate-500 w-full">{msg}</p>}
    </form>
  );
}

function MemberPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const timer = useRef(null);
  const inputRef = useRef(null);

  function updateDropdownPos() {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 288) });
  }

  function handleChange(e) {
    const v = e.target.value;
    setQ(v);
    setPicked(null);
    onPick(null);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      membersAPI.search(v).then((res) => {
        setResults(res.members || []);
        updateDropdownPos();
      }).catch(() => setResults([]));
    }, 300);
  }

  function choose(m) {
    setPicked(m);
    setQ(`${m.full_name} (${m.roll_no || m.id})`);
    setResults([]);
    onPick(m.id);
  }

  // The results list is rendered outside the table's overflow-x-auto wrapper
  // via a portal, positioned to the input's screen coordinates — nesting it
  // inside the table clips it (overflow-x:auto also clips the y-axis once
  // set), which is why it used to render as an invisible sliver you had to
  // scroll a cramped box to see.
  const dropdown = results.length > 0 && dropdownPos && createPortal(
    <div
      style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
      className="z-50 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
    >
      {results.map((m) => (
        <button key={m.id} type="button" onClick={() => choose(m)}
          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50">
          {m.full_name} — roll no. {m.roll_no || '—'} — {m.phone || 'no phone'}
        </button>
      ))}
    </div>,
    document.body
  );

  return (
    <div className="relative">
      <input ref={inputRef} type="text" value={q} onChange={handleChange} placeholder="Search member by name or roll no…"
        className="w-56 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" />
      {dropdown}
      {picked && <p className="mt-1 text-[10px] text-emerald-600">Selected: {picked.full_name}</p>}
    </div>
  );
}

export default function BankReconciliation() {
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('matched_pending');
  const [loading, setLoading] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [pickedMember, setPickedMember] = useState({});
  const [bulkMsg, setBulkMsg] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestMsg, setSuggestMsg] = useState('');
  const [importThreshold, setImportThreshold] = useState(100);

  function loadBatches(selectId) {
    bankReconciliationAPI.listBatches().then((res) => {
      const list = res.batches || [];
      setBatches(list);
      if (selectId) setBatchId(selectId);
      else if (!batchId && list.length) setBatchId(list[0].id);
    });
  }

  function loadBatch() {
    if (!batchId) return;
    setLoading(true);
    bankReconciliationAPI.getBatch(batchId).then((res) => {
      setBatch(res.batch);
      setItems(res.items || []);
    }).finally(() => setLoading(false));
  }

  useEffect(loadBatches, []);
  useEffect(loadBatch, [batchId]);

  async function handleMarkPaid(itemId) {
    setRowBusy((s) => ({ ...s, [itemId]: true }));
    try {
      await bankReconciliationAPI.markPaid(itemId);
      loadBatch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark paid.');
    } finally {
      setRowBusy((s) => ({ ...s, [itemId]: false }));
    }
  }

  async function handleMarkAllPaid() {
    if (!batchId) return;
    if (!window.confirm('Mark ALL pending-matched items in this batch as paid? This texts every affected member.')) return;
    setLoading(true);
    setBulkMsg('');
    try {
      const res = await bankReconciliationAPI.markPaidAll(batchId);
      setBulkMsg(res.message || 'Done.');
      loadBatch();
      loadBatches();
    } catch (err) {
      setBulkMsg(err.response?.data?.error || 'Bulk action failed.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSuggestMembers() {
    if (!batchId) return;
    setSuggesting(true);
    setSuggestMsg('');
    try {
      const res = await bankReconciliationAPI.suggestMembers(batchId);
      setSuggestMsg(res.message || 'Done.');
      loadBatch();
    } catch (err) {
      setSuggestMsg(err.response?.data?.error || 'Failed to suggest members.');
    } finally {
      setSuggesting(false);
    }
  }

  async function handleImportSuggested() {
    if (!batchId) return;
    if (!window.confirm(`Import every not-found item with a suggested match of at least ${importThreshold}% confidence? Each becomes a new paid record for its suggested member.`)) return;
    setLoading(true);
    setSuggestMsg('');
    try {
      const res = await bankReconciliationAPI.importSuggested(batchId, importThreshold);
      setSuggestMsg(res.message || 'Done.');
      loadBatch();
    } catch (err) {
      setSuggestMsg(err.response?.data?.error || 'Bulk import failed.');
    } finally {
      setLoading(false);
    }
  }

  async function handleImportItemForMember(itemId, memberId) {
    setRowBusy((s) => ({ ...s, [itemId]: true }));
    try {
      await bankReconciliationAPI.importItem(itemId, memberId);
      loadBatch();
    } catch (err) {
      alert(err.response?.data?.error || 'Import failed.');
    } finally {
      setRowBusy((s) => ({ ...s, [itemId]: false }));
    }
  }

  async function handleImport(itemId) {
    const memberId = pickedMember[itemId];
    if (!memberId) {
      alert('Pick a member first.');
      return;
    }
    await handleImportItemForMember(itemId, memberId);
  }

  async function handleAcceptAmount(item) {
    if (!window.confirm(`Correct the invoice amount to TZS ${Number(item.amount).toLocaleString()} (the bank amount) and mark it paid?`)) return;
    setRowBusy((s) => ({ ...s, [item.id]: true }));
    try {
      const res = await bankReconciliationAPI.acceptAmount(item.id);
      alert(res.message);
      loadBatch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept amount.');
    } finally {
      setRowBusy((s) => ({ ...s, [item.id]: false }));
    }
  }

  async function handleSplitRemainder(item) {
    const remainder = Number(item.system_amount) - Number(item.amount);
    if (!window.confirm(`Mark TZS ${Number(item.amount).toLocaleString()} as paid on ${item.reference_no}, and create a NEW control number for the remaining TZS ${remainder.toLocaleString()}?`)) return;
    setRowBusy((s) => ({ ...s, [item.id]: true }));
    try {
      const res = await bankReconciliationAPI.splitRemainder(item.id);
      alert(res.message);
      loadBatch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to split remainder.');
    } finally {
      setRowBusy((s) => ({ ...s, [item.id]: false }));
    }
  }

  async function handleSkip(itemId) {
    setRowBusy((s) => ({ ...s, [itemId]: true }));
    try {
      await bankReconciliationAPI.skipItem(itemId);
      loadBatch();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to skip.');
    } finally {
      setRowBusy((s) => ({ ...s, [itemId]: false }));
    }
  }

  const visibleItems = items.filter((i) => i.match_status === activeTab);
  const tabCount = (key) => items.filter((i) => i.match_status === key).length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Bank Reconciliation</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Upload a CRDB account statement to match TLSD control-number transactions against invoices.
        </p>
      </div>

      <UploadCard onUploaded={(id) => loadBatches(id)} />

      {batches.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <label className="text-xs font-semibold text-slate-500">Batch:</label>
          <select value={batchId || ''} onChange={(e) => setBatchId(Number(e.target.value))}
            className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs">
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                #{b.id} — {b.filename} — {b.total_rows} rows — {b.created_at}
              </option>
            ))}
          </select>
        </div>
      )}

      {batch && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex overflow-x-auto border-b border-slate-100 bg-slate-50">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 ${
                    activeTab === t.key ? 'border-orange-600 text-orange-700' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}>
                  {t.label} ({tabCount(t.key)})
                </button>
              ))}
            </div>

            {activeTab === 'matched_pending' && tabCount('matched_pending') > 0 && (
              <div className="p-3 border-b border-slate-100 flex items-center gap-3">
                <button onClick={handleMarkAllPaid} disabled={loading}
                  className="rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
                  ✓ Mark ALL {tabCount('matched_pending')} as Paid
                </button>
                {bulkMsg && <p className="text-xs text-slate-500">{bulkMsg}</p>}
              </div>
            )}

            {activeTab === 'not_found' && tabCount('not_found') > 0 && (
              <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
                <button onClick={handleSuggestMembers} disabled={suggesting}
                  className="rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
                  {suggesting ? 'Matching names…' : '🔍 Auto-suggest members by name'}
                </button>
                <div className="flex items-center gap-1.5">
                  <label className="text-xs text-slate-500">Import matches ≥</label>
                  <select value={importThreshold} onChange={(e) => setImportThreshold(Number(e.target.value))}
                    className="rounded-lg border border-slate-200 px-2 py-1 text-xs">
                    <option value={100}>100% (exact)</option>
                    <option value={80}>80%</option>
                    <option value={60}>60%</option>
                    <option value={40}>40% (all suggested)</option>
                  </select>
                  <button onClick={handleImportSuggested} disabled={loading}
                    className="rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-3 py-1.5 text-xs font-semibold">
                    Import all
                  </button>
                </div>
                {suggestMsg && <p className="text-xs text-slate-500 w-full">{suggestMsg}</p>}
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 bg-slate-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">Date</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">Reference</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">Payer</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">Amount</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleItems.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-slate-400">No items in this tab.</td></tr>
                  ) : visibleItems.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-xs text-slate-500 font-mono whitespace-nowrap">{item.posting_date}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-700 font-mono">{item.reference_no}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        {item.payer_name || '—'}{item.phone ? ` · ${item.phone}` : ''}
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        <span className="font-semibold text-slate-800">{money(item.amount)}</span>
                        {item.match_status === 'mismatch' && item.system_amount != null && (
                          <span className="block text-[10px] text-slate-400">system expects {money(item.system_amount)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {item.match_status === 'matched_pending' && (
                          <button onClick={() => handleMarkPaid(item.id)} disabled={rowBusy[item.id]}
                            className="rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-3 py-1 text-xs font-semibold">
                            {rowBusy[item.id] ? '…' : 'Mark Paid'}
                          </button>
                        )}
                        {item.match_status === 'not_found' && (
                          <div className="space-y-1.5">
                            {item.suggested_member_name && (
                              <div className="flex items-center gap-2">
                                <span className="text-[11px] text-emerald-700">
                                  Suggested: <strong>{item.suggested_member_name}</strong> (roll {item.suggested_member_roll_no || '—'}) · {Number(item.suggested_confidence).toFixed(0)}%
                                </span>
                                <button onClick={() => handleImportItemForMember(item.id, item.suggested_member_id)}
                                  disabled={rowBusy[item.id]}
                                  className="rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white px-2.5 py-1 text-[11px] font-semibold">
                                  ✓ Use this
                                </button>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <MemberPicker onPick={(id) => setPickedMember((s) => ({ ...s, [item.id]: id }))} />
                              <button onClick={() => handleImport(item.id)} disabled={rowBusy[item.id] || !pickedMember[item.id]}
                                className="rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-3 py-1 text-xs font-semibold">
                                Import
                              </button>
                              <button onClick={() => handleSkip(item.id)} disabled={rowBusy[item.id]}
                                className="rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-60 px-3 py-1 text-xs font-semibold">
                                Skip
                              </button>
                            </div>
                          </div>
                        )}
                        {item.match_status === 'mismatch' && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <button onClick={() => handleAcceptAmount(item)} disabled={rowBusy[item.id]}
                              className="rounded-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-2.5 py-1 text-[11px] font-semibold">
                              Accept TZS {Number(item.amount).toLocaleString()} → Paid
                            </button>
                            {item.system_amount != null && Number(item.amount) < Number(item.system_amount) && (
                              <button onClick={() => handleSplitRemainder(item)} disabled={rowBusy[item.id]}
                                className="rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white px-2.5 py-1 text-[11px] font-semibold">
                                Paid → split remainder to new control no.
                              </button>
                            )}
                            <button onClick={() => handleSkip(item.id)} disabled={rowBusy[item.id]}
                              className="rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-60 px-2.5 py-1 text-[11px] font-semibold">
                              Skip
                            </button>
                          </div>
                        )}
                        {['already_paid', 'resolved_paid', 'resolved_imported', 'skipped'].includes(item.match_status) && (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
